import React, {useEffect, useMemo, useRef} from 'react';
import {LayersControl, MapContainer, Marker, TileLayer, useMap, useMapEvents} from 'react-leaflet';
import L, {LatLngTuple} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {GpsCoordinates} from '../Types/types';

const DEFAULT_CENTER: LatLngTuple = [46.6, 2.5];
const DEFAULT_ZOOM = 6;
const POSITION_ZOOM = 17;

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface AdresseMapProps {
  position: GpsCoordinates | null;
  onPositionChange: (coordinates: GpsCoordinates) => void;
}

const Recenter = ({position}: {position: GpsCoordinates | null}): null => {
  const map = useMap();
  const latitude = position?.latitude;
  const longitude = position?.longitude;
  useEffect(() => {
    if (latitude !== undefined && longitude !== undefined) {
      map.setView([latitude, longitude], Math.max(map.getZoom(), POSITION_ZOOM));
    }
  }, [map, latitude, longitude]);
  return null;
};

const ClickHandler = ({onPick}: {onPick: (c: GpsCoordinates) => void}): null => {
  useMapEvents({
    click: (e) => onPick({latitude: e.latlng.lat, longitude: e.latlng.lng}),
  });
  return null;
};

const AdresseMap = ({position, onPositionChange}: AdresseMapProps): JSX.Element => {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend: () => {
        const latlng = markerRef.current?.getLatLng();
        if (latlng) onPositionChange({latitude: latlng.lat, longitude: latlng.lng});
      },
    }),
    [onPositionChange],
  );

  return (
    <MapContainer
      center={position ? [position.latitude, position.longitude] : DEFAULT_CENTER}
      zoom={position ? POSITION_ZOOM : DEFAULT_ZOOM}
      style={{height: 300, width: '100%'}}
      className="rounded"
    >
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Plan">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      <Recenter position={position} />
      <ClickHandler onPick={onPositionChange} />
      {position && (
        <Marker
          draggable
          icon={defaultIcon}
          position={[position.latitude, position.longitude]}
          ref={markerRef}
          eventHandlers={eventHandlers}
        />
      )}
    </MapContainer>
  );
};

export default AdresseMap;
